'use client';

import { RadioButton, SearchIcon } from './Controls';
import { MIN_SEARCH_LENGTH, SORT_OPTIONS, SortOption } from './types';

const DEFAULT_SEARCH_INPUT_CLASS =
  'flex-1 bg-transparent text-lg font-semibold text-black placeholder:font-semibold placeholder:text-black/50 focus:outline-none';
const DEFAULT_SORT_LABEL_CLASS = 'text-sm font-semibold';

export function SearchAndSort({
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  isSearchActive,
  effectiveSortBy,
  onSortChange,
  searchInputClassName = DEFAULT_SEARCH_INPUT_CLASS,
  sortLabelClassName = DEFAULT_SORT_LABEL_CLASS,
}: {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  isSearchActive: boolean;
  effectiveSortBy: SortOption;
  onSortChange: (value: SortOption) => void;
  /** Overrides the search <input> typography/placeholder styling. */
  searchInputClassName?: string;
  /** Overrides the sort-option text styling (color still switches on active). */
  sortLabelClassName?: string;
}) {
  return (
    <>
      {/* Search bar */}
      <div className="flex items-center gap-3 rounded-2xl border border-[#ccc] bg-white px-4 py-3 backdrop-blur-[21px]">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className={searchInputClassName}
          aria-label="Search resources"
          maxLength={100}
        />
        <SearchIcon />
      </div>

      {/* Sort options */}
      <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:gap-4">
        {SORT_OPTIONS.map((opt) => {
          const disabled = isSearchActive && opt.value !== 'newest';
          return (
            <label
              key={opt.value}
              className={`flex items-center gap-2 ${
                disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
              }`}
              title={
                disabled
                  ? 'For best match results we order by newest articles first'
                  : undefined
              }
            >
              <RadioButton
                checked={effectiveSortBy === opt.value}
                onChange={() => {
                  if (!disabled) {
                    onSortChange(opt.value);
                  }
                }}
              />
              <span
                className={`${sortLabelClassName} ${
                  effectiveSortBy === opt.value
                    ? 'text-[#4b0fea]'
                    : 'text-black'
                }`}
              >
                {opt.label}
              </span>
            </label>
          );
        })}
      </div>

      {/* Minimum search length hint */}
      {searchQuery.trim().length > 0 &&
        searchQuery.trim().length < MIN_SEARCH_LENGTH && (
          <p className="text-sm text-gray-500">
            Please enter at least {MIN_SEARCH_LENGTH} characters to search
          </p>
        )}
    </>
  );
}
